import React, { useState, useRef } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './InlineCropper.css';

function InlineCropper({ imageSrc, onCropComplete, onCancel, aspectRatio }) {
  const [crop, setCrop] = useState({
    unit: '%',
    width: 80,
    height: 80,
    x: 10,
    y: 10,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);

  const getCroppedImg = async () => {
    if (!completedCrop || !imgRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Use NATURAL size for canvas to preserve quality
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(null);
            return;
          }
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            resolve(reader.result);
          };
        },
        'image/jpeg',
        0.95
      );
    });
  };

  const handleSave = async () => {
    const croppedImage = await getCroppedImg();
    if (croppedImage) {
      onCropComplete(croppedImage);
    }
  };

  const handleUseFullImage = () => {
    onCropComplete(imageSrc);
  };

  return (
    <div className="inline-cropper-container">
      <div className="inline-crop-area">
        <ReactCrop
          crop={crop}
          onChange={(c) => setCrop(c)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={aspectRatio}
        >
          <img
            ref={imgRef}
            src={imageSrc}
            alt="Crop preview"
            style={{ maxWidth: '100%', maxHeight: '400px' }}
            onLoad={() => {
              // Set initial crop when image loads
              if (imgRef.current) {
                const { width, height } = imgRef.current;
                let cropWidth, cropHeight;
                if (aspectRatio) {
                  // Fit a locked-ratio crop into the image, using 80% of the
                  // wider axis as the starting size.
                  cropWidth = width * 0.8;
                  cropHeight = cropWidth / aspectRatio;
                  if (cropHeight > height * 0.9) {
                    cropHeight = height * 0.8;
                    cropWidth = cropHeight * aspectRatio;
                  }
                } else {
                  cropWidth = Math.min(width * 0.8, width);
                  cropHeight = Math.min(height * 0.8, height);
                }
                setCrop({
                  unit: 'px',
                  width: cropWidth,
                  height: cropHeight,
                  x: (width - cropWidth) / 2,
                  y: (height - cropHeight) / 2,
                });
              }
            }}
          />
        </ReactCrop>
      </div>

      <div className="inline-crop-actions">
        <button type="button" className="btn-crop-cancel" onClick={onCancel}>
          ✕ Cancel
        </button>
        <button
          type="button"
          className="btn-crop-full"
          onClick={handleUseFullImage}
        >
          📷 Use Full Image
        </button>
        <button type="button" className="btn-crop-save" onClick={handleSave}>
          ✓ Lock In Crop
        </button>
      </div>

      <p className="inline-crop-hint">
        💡 Drag corners and edges to resize, drag inside to move
      </p>
    </div>
  );
}

export default InlineCropper;
